import React, {PropsWithChildren, useEffect} from 'react';
import {useQuery} from '@tanstack/react-query';
import ChekinProSettings from 'ChekinProSettings';
import {API} from '@chekin/api';
import {
  BookingSummary,
  SUMMARY_PAYMENTS_STATUSES,
  LocalStorage,
  QUERY_CACHE_KEYS,
} from '@chekin/core';
import {useChekinSDK} from '../ChekinSDK';
import {useReservation} from '../reservation';
import {useCustomTheme} from '../customTheme';
import {useKioskoStore} from 'context/kiosko';
import {PATHS} from 'routes/constants';
import {RESERVATION_ID_KEY} from 'utils/constants';
import {checkIsPaymentNotComplete} from './utils';

enum BASE_PATHS {
  home = 'home',
  onlineChekin = 'onlineChekin',
}

export type ContextProps = {
  housingName: string;
  countryCode: string;
  reservationId: string;
  isGuestappComplete: boolean;
  checkIfAnyPaymentNotComplete: (exemption?: 'insurance' | 'taxes') => boolean;
  data?: BookingSummary;
  error?: Error | null;
  arePaymentsAvailable?: boolean;
  isLoading?: boolean;
  isFetching?: boolean;
  isLoadingError?: boolean;
  isSuccess?: boolean;
  isGuestRegistrationOnly: boolean;
  isGuestRegistrationComplete: boolean;
  basePaths: {[key in BASE_PATHS]: string};
  areAllPaymentsCompleted?: boolean;
  summaryStatus: 'error' | 'success' | 'loading' | 'pending';
  isSecurityDepositPending: boolean;
  isInstantCheckInEnabled: boolean;
};

const SummaryContext = React.createContext<ContextProps>({
  data: undefined,
  isLoading: false,
  isFetching: false,
  countryCode: '',
  reservationId: '',
  housingName: '',
  isGuestappComplete: false,
  checkIfAnyPaymentNotComplete: () => true,
  isGuestRegistrationOnly: true,
  isGuestRegistrationComplete: false,
  isInstantCheckInEnabled: false,
  basePaths: {[BASE_PATHS.home]: '', [BASE_PATHS.onlineChekin]: ''},
  summaryStatus: 'loading',
  isSecurityDepositPending: true,
});

function SummaryProvider({children}: Readonly<PropsWithChildren>) {
  const {isTokenValid} = useChekinSDK();
  const {isTemplateInitialized} = useCustomTheme();
  const {data: reservation} = useReservation();
  const [isReadyToken, setIsReadyToken] = React.useState(false);
  const isValidTokenTimeoutRef = React.useRef<NodeJS.Timeout>();
  const isKiosko = useKioskoStore(state => state.connection.isConnected);
  const setCardsState = useKioskoStore(state => state.setCardsState);
  const reservationId = isTemplateInitialized
    ? reservation?.id ?? LocalStorage.get<string>(RESERVATION_ID_KEY) ?? ''
    : '';

  React.useEffect(() => {
    isValidTokenTimeoutRef.current = setTimeout(() => {
      if (isTokenValid) {
        setIsReadyToken(true);
      }
    }); // we have to improve this, the bug is in sdk, in AUTHSERVICE, in the init function, that is
    //the function who responde with the isValid token, but, currently, is giving us a isValidToken in TRUE
    // but the token is not jet in the localstorage, so, we make a couple a fails rquest without tokenn
    //so, with this timeout we aboid temporaly the race condition, but the true fix is, have this "isValidToken"
    //in true, at the moment when we are pretty sure that the token could be accesed from the localstorage/
  }, [isTokenValid, setIsReadyToken, reservationId]);
  // React.useEffect(() => {
  //   return () => {
  //     clearTimeout(isValidTokenTimeoutRef.current);
  //   };
  // }, []);
  const enabled = isReadyToken && Boolean(reservationId);
  const {
    data,
    error,
    isLoading: isInternalLoading,
    isFetching,
    isLoadingError,
    isSuccess,
    status,
  } = useQuery<BookingSummary, Error>({
    // eslint-disable-next-line @tanstack/query/exhaustive-deps
    queryKey: [QUERY_CACHE_KEYS.summary],
    queryFn: () => API.reservation.getSummary(reservationId || ''),
    refetchOnWindowFocus: false,
    refetchOnMount: false,
    enabled,
  });

  const isLoading = Boolean(reservationId && isInternalLoading);

  const summary = React.useMemo(() => data || ({} as BookingSummary), [data]);
  const isSecurityDepositPending =
    data?.property_protection.status === SUMMARY_PAYMENTS_STATUSES.configurationPending;
  const isGuestRegistrationOnly = [
    summary?.property_protection,
    summary?.taxes,
    summary?.upselling,
  ].every(feature => !feature?.active);

  const countryCode = React.useMemo(
    function getHousingCountryCode(): string {
      const code = data?.country_code;

      if (!code) {
        return '';
      }

      return code;
    },
    [data?.country_code],
  );

  const arePaymentsAvailable = React.useMemo(() => {
    return [
      data?.property_protection,
      data?.taxes,
      data?.booking_payments,
      data?.upselling,
    ].some(payment => payment?.active);
  }, [data?.booking_payments, data?.property_protection, data?.taxes, data?.upselling]);

  const housingName = summary.property_name || '';
  const checkIfAnyPaymentNotComplete = React.useCallback(
    (exemption?: 'insurance' | 'taxes') => {
      return (
        (checkIsPaymentNotComplete(summary.property_protection) &&
          exemption !== 'insurance') ||
        (checkIsPaymentNotComplete(summary.taxes) && exemption !== 'taxes')
      );
    },
    [summary.property_protection, summary.taxes],
  );

  const basePaths = React.useMemo(() => {
    return {
      [BASE_PATHS.home]: PATHS.home,
      [BASE_PATHS.onlineChekin]: isGuestRegistrationOnly
        ? PATHS.guestRegistration
        : PATHS.home,
    };
  }, [isGuestRegistrationOnly]);

  const areAllPaymentsCompleted = React.useMemo(() => {
    return [data?.booking_payments, data?.property_protection, data?.taxes]
      .filter(payment => payment?.active)
      .every(
        payment =>
          payment?.status === SUMMARY_PAYMENTS_STATUSES.complete ||
          payment?.status === SUMMARY_PAYMENTS_STATUSES.payOnArrival,
      );
  }, [data?.booking_payments, data?.property_protection, data?.taxes]);
  const isGuestRegistrationComplete = data?.guest_registration?.status === 'COMPLETE';

  const isInstantCheckInEnabled =
    Boolean(data?.is_instant_check_in_enabled) && !ChekinProSettings.isSDK;
  const isGuestappComplete = arePaymentsAvailable
    ? areAllPaymentsCompleted && isGuestRegistrationComplete
    : isGuestRegistrationComplete;

  useEffect(() => {
    if (summary?.id && isGuestappComplete) {
      ChekinProSettings.events.onAllRequiredStepsCompleted();
    }
  }, [isGuestappComplete, summary?.id]);

  useEffect(() => {
    if (isKiosko && summary.expected_guests) {
      setCardsState({pending: summary.expected_guests});
    }
  }, [isKiosko, setCardsState, summary.expected_guests, summary]);

  const defaultProps = React.useMemo<ContextProps>(
    () => ({
      data: summary,
      error,
      isLoading,
      isFetching,
      isLoadingError,
      countryCode,
      reservationId,
      isSuccess,
      housingName,
      isGuestRegistrationComplete,
      isGuestappComplete,
      arePaymentsAvailable,
      checkIfAnyPaymentNotComplete,
      isGuestRegistrationOnly,
      basePaths,
      areAllPaymentsCompleted,
      summaryStatus: status,
      isInstantCheckInEnabled,
      isSecurityDepositPending,
    }),
    [
      isGuestappComplete,
      checkIfAnyPaymentNotComplete,
      countryCode,
      error,
      arePaymentsAvailable,
      housingName,
      isFetching,
      isLoading,
      isLoadingError,
      isSuccess,
      reservationId,
      summary,
      isGuestRegistrationOnly,
      basePaths,
      isGuestRegistrationComplete,
      areAllPaymentsCompleted,
      status,
      isInstantCheckInEnabled,
      isSecurityDepositPending,
    ],
  );

  return <SummaryContext.Provider value={defaultProps} children={children} />;
}

export {SummaryContext, SummaryProvider};
